'use client';

import { Icon } from '@iconify-icon/react';
import Link from 'next/link';

import PageLink from '@/utils/PageLink';

const steps = [
  {
    numeral: '1',
    title: 'Drop in your knowledge',
    body: 'Upload files, paste a link to your website, or connect Google Drive and OneDrive — your content flows in and stays in sync. If you can upload a file, you can do this.',
    chips: [
      { icon: 'logos:google-drive', label: 'Google Drive' },
      { icon: 'logos:microsoft-onedrive', label: 'OneDrive' },
      { icon: 'tabler:world-www', label: 'Your website' },
      { icon: 'tabler:file-type-pdf', label: 'PDF & Office' },
    ],
  },
  {
    numeral: '2',
    title: 'Make it yours',
    body: 'Name it, pick its personality, match your brand colors. Under the hood you get 10+ leading AI models — switch anytime without retraining a thing.',
    chips: [
      { icon: 'tabler:palette', label: 'Your branding' },
      { icon: 'tabler:mood-smile', label: 'Personality & tone' },
      { icon: 'tabler:sparkles', label: '10+ AI models' },
    ],
  },
  {
    numeral: '3',
    title: 'Go live in minutes',
    body: 'Copy one snippet onto your site, share a link, or scan a QR code. No deploy pipeline, no ticket to engineering — it just works.',
    chips: [
      { icon: 'tabler:code', label: 'One-line embed' },
      { icon: 'tabler:link', label: 'Share a link' },
      { icon: 'tabler:qrcode', label: 'QR code' },
    ],
  },
];

const integrations: { icon: string; label: string; iconClass?: string }[] = [
  { icon: 'logos:google-drive', label: 'Google Drive' },
  { icon: 'logos:microsoft-onedrive', label: 'OneDrive' },
  // `logos:` has no SharePoint mark — use the monochrome MDI glyph in brand teal
  { icon: 'mdi:microsoft-sharepoint', label: 'SharePoint', iconClass: 'text-[#038387]' },
  { icon: 'logos:notion-icon', label: 'Notion' },
  { icon: 'logos:slack-icon', label: 'Slack' },
  { icon: 'logos:microsoft-teams', label: 'Teams' },
  { icon: 'logos:whatsapp-icon', label: 'WhatsApp' },
  { icon: 'logos:telegram', label: 'Telegram' },
  { icon: 'logos:discord-icon', label: 'Discord' },
  { icon: 'logos:zendesk-icon', label: 'Zendesk' },
];

const HowItWorksCompact: React.FC = () => {
  return (
    <section aria-label="How it works" className="bg-white py-20 sm:py-24" id="how-it-works">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Editorial header */}
        <div className="max-w-2xl">
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-primary-600/50" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary-600">
              How it works
            </span>
          </div>
          <h2 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-gray-900 sm:text-[2.6rem] sm:leading-[1.15]">
            Live in minutes.{' '}
            <span className="font-display font-medium italic text-primary-600">Not months.</span>
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-gray-500">
            You don&apos;t have to be an engineer. Three steps, no code, and the first one is just
            uploading a file.
          </p>
        </div>

        {/* Steps — asymmetric editorial rows */}
        <div className="mt-14 divide-y divide-gray-100 border-y border-gray-100">
          {steps.map((step) => (
            <div
              key={step.numeral}
              className="group grid gap-6 py-10 transition-colors duration-300 hover:bg-[#f8fafc] sm:grid-cols-12 sm:gap-8 sm:px-4">
              <div className="sm:col-span-2">
                <span className="font-display text-6xl font-light italic leading-none text-gray-200 transition-colors duration-300 group-hover:text-primary-600/40 sm:text-7xl">
                  {step.numeral}
                </span>
              </div>
              <div className="sm:col-span-4">
                <h3 className="text-xl font-bold text-gray-900">{step.title}</h3>
              </div>
              <div className="sm:col-span-6">
                <p className="text-[15px] leading-relaxed text-gray-500">{step.body}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {step.chips.map((chip) => (
                    <span
                      key={chip.label}
                      className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-600">
                      <Icon icon={chip.icon} className="text-sm" />
                      {chip.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Integrations marquee */}
        <div className="mt-12">
          <p className="text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400">
            Plays nicely with your stack
          </p>
          <div className="relative mt-5 overflow-hidden">
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-20 bg-gradient-to-r from-white to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-20 bg-gradient-to-l from-white to-transparent" />
            <div className="flex w-max animate-marquee gap-10">
              {[...integrations, ...integrations].map((tool, i) => (
                <span
                  key={`${tool.label}-${i}`}
                  className="flex items-center gap-2 text-sm font-medium text-gray-400">
                  <Icon icon={tool.icon} className={`text-xl ${tool.iconClass ?? ''}`} />
                  {tool.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center gap-3">
          <Link
            href={PageLink.login}
            className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-8 py-3 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/25">
            Start 14-Day Trial
            <Icon icon="tabler:arrow-right" className="text-base" />
          </Link>
          <span className="text-sm text-gray-400">No credit card required</span>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksCompact;
